import React, { useEffect, useState, useCallback } from "react";
import { Brain, Search, ShieldCheck, AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Sparkles, FileCheck, TrendingDown } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { AdminShell } from "@/components/AdminSidebar";

const CONF = {
  VERIFIED: "text-race-volt border-race-volt/40",
  HIGH: "text-emerald-400 border-emerald-400/40",
  MEDIUM: "text-amber-400 border-amber-400/40",
  LOW: "text-race-sub border-race-line",
};
const SEV = {
  CRITICAL: "text-race-red border-race-red",
  HIGH: "text-race-red border-race-red/50",
  MEDIUM: "text-amber-400 border-amber-400/40",
  LOW: "text-race-sub border-race-line",
};

export default function DebugIntelligence() {
  const [stats, setStats] = useState(null);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [obs, setObs] = useState("");
  const [assist, setAssist] = useState(null);
  const [assisting, setAssisting] = useState(false);
  const [open, setOpen] = useState(null);
  const [sops, setSops] = useState([]);
  const [trend, setTrend] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/agent-learning/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/agent-learning/bugs").then((r) => setBugs(r.data)).catch(() => {}),
      api.get("/agent-learning/sops").then((r) => setSops(r.data)).catch(() => {}),
      api.get("/agent-learning/trend").then((r) => setTrend(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const decideSop = (sopId, decision) => {
    api.post(`/agent-learning/sops/${sopId}/decision`, { decision })
      .then(() => load()).catch(() => {});
  };

  const runAssist = () => {
    if (!obs.trim()) return;
    setAssisting(true);
    api.post("/agent-learning/assist", { observation: obs })
      .then((r) => setAssist(r.data)).catch(() => {}).finally(() => setAssisting(false));
  };

  return (
    <AdminShell>
      <div className="animate-slide-up" data-testid="debug-intel-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <Brain size={20} className="text-race-red" />
          <h1 className="font-heading text-3xl">Debugging Intelligence</h1>
        </div>
        <button onClick={load} data-testid="di-refresh" className="flex items-center gap-1.5 text-sm border border-race-line hover:border-race-txt rounded-sm px-3 py-1.5 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-race-sub text-sm mb-6">Institutional memory for the multi-agent platform. Search before you debug - reuse verified fixes, avoid past mistakes.</p>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8" data-testid="di-stats">
          <Stat label="Bugs logged" value={stats.total_bugs} />
          <Stat label="Verified" value={stats.verified} accent />
          <Stat label="Repeat rate" value={`${Math.round(stats.repeat_bug_rate * 100)}%`} />
          <Stat label="Systemic patterns" value={stats.systemic_patterns} />
        </div>
      )}

      {/* ASSIST - "Have we seen this before?" */}
      <div className="bg-race-surface border border-race-line rounded-lg p-5 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-race-red" />
          <h2 className="text-base font-semibold">Have we seen this before?</h2>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="di-assist-input"
            value={obs}
            onChange={(e) => setObs(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runAssist()}
            placeholder="Describe the bug or symptom in plain words…"
            className="flex-1 bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-sm" />
          <button onClick={runAssist} disabled={assisting} data-testid="di-assist-btn"
            className="flex items-center gap-1.5 bg-race-red text-race-bg font-semibold px-4 rounded-sm text-sm disabled:opacity-50">
            <Search size={15} /> {assisting ? "Searching…" : "Search"}
          </button>
        </div>

        {assist && (
          <div className="mt-4 text-sm" data-testid="di-assist-result">
            <div className={`flex items-center gap-2 mb-2 ${assist.seen_before ? "text-race-volt" : "text-race-sub"}`}>
              {assist.seen_before ? <ShieldCheck size={16} /> : <AlertTriangle size={16} />}
              <span className="font-semibold">
                {assist.seen_before ? "Yes - this resembles a known issue." : "No strong match - proceed with fresh diagnosis."}
              </span>
            </div>
            <p className="text-race-txt bg-race-bg border border-race-line rounded-sm px-3 py-2 mb-3">{assist.recommendation}</p>

            {assist.verified_resolutions.length > 0 && (
              <div className="mb-3">
                <div className="overline text-race-sub mb-1">Verified resolutions</div>
                {assist.verified_resolutions.map((v) => (
                  <div key={v.bug_id} className="border border-race-volt/30 rounded-sm px-3 py-2 mb-2" data-testid={`di-vres-${v.bug_id}`}>
                    <div className="font-mono text-xs text-race-volt">{v.bug_id} · match {Math.round(v.score * 100)}%</div>
                    <div className="text-race-txt mt-1">{v.resolution}</div>
                    {v.prevention && <div className="text-xs text-race-sub mt-1"><b>Prevention:</b> {v.prevention}</div>}
                  </div>
                ))}
              </div>
            )}

            {assist.failed_attempts.length > 0 && (
              <div>
                <div className="overline text-race-sub mb-1">Don't repeat these failed attempts</div>
                {assist.failed_attempts.map((f, i) => (
                  <div key={i} className="text-xs text-race-sub border-l-2 border-race-red/40 pl-2 mb-1">
                    <span className="font-mono">{f.bug_id}</span>: {f.hypothesis} → <b className="text-race-red">{f.result}</b> ({f.reason})
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* TREND - watch bug frequency fall over time */}
      {trend.length > 0 && (
        <div className="bg-race-surface border border-race-line rounded-lg p-5 mb-8" data-testid="di-trend">
          <div className="flex items-center gap-2 mb-3">
            <TrendingDown size={16} className="text-race-volt" />
            <h2 className="text-base font-semibold">Bug frequency over time</h2>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <AreaChart data={trend} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gOcc" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E4322B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#E4322B" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gNew" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C9F14A" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C9F14A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#26262b" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#8a8a93", fontSize: 11 }} stroke="#26262b" />
                <YAxis allowDecimals={false} tick={{ fill: "#8a8a93", fontSize: 11 }} stroke="#26262b" />
                <Tooltip contentStyle={{ background: "#141418", border: "1px solid #26262b", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#e7e7ea" }} />
                <Area type="monotone" dataKey="occurrences" name="Occurrences" stroke="#E4322B" fill="url(#gOcc)" strokeWidth={2} />
                <Area type="monotone" dataKey="new_bugs" name="New bugs" stroke="#C9F14A" fill="url(#gNew)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-race-sub mt-1">As fixes + prevention take hold, occurrences of known failure classes should trend down.</p>
        </div>
      )}

      {/* SOP PROPOSALS - Prime approval */}
      {sops.length > 0 && (
        <div className="mb-8" data-testid="di-sop-section">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck size={16} className="text-race-red" />
            <h2 className="text-base font-semibold">SOP Proposals <span className="text-race-sub">- Prime approval</span></h2>
          </div>
          <div className="space-y-2">
            {sops.map((s) => (
              <div key={s.sop_id} className="bg-race-surface border border-race-line rounded-md px-4 py-3" data-testid={`di-sop-${s.sop_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] text-race-sub">{s.sop_id} · {s.occurrences}× · {s.related_bug_ids?.join(", ")}</div>
                    <div className="font-medium">{s.title}</div>
                  </div>
                  {s.status === "PROPOSED" ? (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => decideSop(s.sop_id, "approved")} data-testid={`di-sop-approve-${s.sop_id}`}
                        className="text-xs font-semibold bg-race-volt/90 text-race-bg rounded-sm px-3 py-1.5">Approve</button>
                      <button onClick={() => decideSop(s.sop_id, "rejected")} data-testid={`di-sop-reject-${s.sop_id}`}
                        className="text-xs font-semibold border border-race-line hover:border-race-red text-race-sub rounded-sm px-3 py-1.5">Reject</button>
                    </div>
                  ) : (
                    <span className={`text-[10px] font-mono uppercase border rounded-full px-2 py-0.5 ${s.status === "APPROVED" ? "text-race-volt border-race-volt/40" : "text-race-sub border-race-line"}`}>{s.status}</span>
                  )}
                </div>
                <ul className="mt-2 space-y-1">
                  {s.steps?.map((st, i) => <li key={i} className="text-xs text-race-sub flex gap-2"><span className="text-race-red">{i + 1}.</span>{st}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LIBRARY */}
      <h2 className="text-base font-semibold mb-3">Bug Resolution Library <span className="text-race-sub">({bugs.length})</span></h2>
      <div className="space-y-2" data-testid="di-bug-list">
        {bugs.map((b) => (
          <div key={b.bug_id} className="bg-race-surface border border-race-line rounded-md overflow-hidden">
            <button
              onClick={() => setOpen(open === b.bug_id ? null : b.bug_id)}
              data-testid={`di-bug-${b.bug_id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-race-bg/40 transition-colors">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-race-sub">{b.bug_id} · {b.agent_group}</div>
                <div className="truncate font-medium">{b.summary}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-mono uppercase border rounded-full px-2 py-0.5 ${SEV[b.severity] || SEV.LOW}`}>{b.severity}</span>
                <span className={`text-[10px] font-mono uppercase border rounded-full px-2 py-0.5 ${CONF[b.confidence] || CONF.LOW}`}>{b.confidence}</span>
                {open === b.bug_id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </div>
            </button>
            {open === b.bug_id && (
              <div className="px-4 pb-4 pt-1 text-sm border-t border-race-line space-y-2" data-testid={`di-detail-${b.bug_id}`}>
                <Field k="Root cause" v={b.root_cause?.root || b.root_cause} />
                <Field k="Systemic cause" v={b.root_cause?.systemic} />
                <Field k="Resolution" v={b.resolution} />
                <Field k="Verification" v={b.verification} />
                <Field k="Regression test" v={b.regression_test} />
                <Field k="Prevention" v={b.prevention} />
                <Field k="Agent learning" v={b.agent_learning} />
                {b.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {b.tags.map((t) => <span key={t} className="text-[10px] font-mono text-race-sub border border-race-line rounded-full px-2 py-0.5">#{t}</span>)}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {!loading && bugs.length === 0 && <p className="text-race-sub text-sm">No bug records yet.</p>}
      </div>
      </div>
    </AdminShell>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="bg-race-surface border border-race-line rounded-md px-4 py-3">
    <div className={`font-heading text-2xl ${accent ? "text-race-volt" : ""}`}>{value}</div>
    <div className="overline text-race-sub mt-0.5">{label}</div>
  </div>
);

const Field = ({ k, v }) => (!v ? null : (
  <div><span className="overline text-race-sub">{k}:</span> <span className="text-race-txt">{typeof v === "string" ? v : JSON.stringify(v)}</span></div>
));
